import {Controller} from "@hotwired/stimulus"
import {createConsumer} from "@rails/actioncable"
import {Feature, Map as OlMap, View} from "ol"
import TileLayer from "ol/layer/Tile"
import {DEVICE_PIXEL_RATIO} from "ol/has"
import {GeoJSON, WMTSCapabilities} from "ol/format"
import {optionsFromCapabilities} from "ol/source/WMTS"
import {WMTS, XYZ} from "ol/source"
import {fromLonLat, transformExtent} from "ol/proj"
import {Polygon} from "ol/geom"
import VectorSource from "ol/source/Vector"
import VectorLayer from "ol/layer/Vector"
import {Fill, Stroke, Style, Text} from "ol/style"
import {Attribution, defaults as defaultControls} from "ol/control"
import LayerSwitcher from "ol-layerswitcher"
import {Group} from "ol/layer"
import {Device} from "./tracker_map/device"
import {MarkerInfo} from "./tracker_map/marker_info"
import {DeckControl} from "./tracker_map/deck_control"
import {Locate} from "./tracker_map/locate"

export class TrackerMapController extends Controller {
    static targets = ["deck", "connectionStatus", "staleThreshold", "searchInput", "searchResults", "locateButton"]
    static values  = {devicesUrl: String, topics: Array, geofences: Array, groupLabels: Object, locateIssi: String}

    static CONNECTION_STATUS_LABELS = {
        connecting:   "Verbinde…",
        connected:    "Live",
        disconnected: "Verbindung getrennt",
        denied:       "Zugriff verweigert"
    }

    CAPABILITIES_URL = "https://mapsneu.wien.gv.at/basemapneu/1.0.0/WMTSCapabilities.xml"

    // event-specific — next event edits these two constants (disabled until next event)
    // NOVAROCK_TILE_BASE     = "https://pics.lsz-b.at/novarock_202606071026"
    // NOVAROCK_EXTENT_LONLAT = [17.0617, 47.9015, 17.1222, 47.9311]

    DISTRICT_BOUNDARIES_URL  = "/tracker/geo_json/district-boundaries_20230627.json"
    CADASTRAL_BOUNDARIES_URL = "/tracker/geo_json/cadastral-boundaries_20230627.json"

    MAP_CENTER               = [16.26888, 47.483716]
    INITIAL_ZOOM             = 9
    ZOOM_LEVEL_FOR_CADASTRAL = 11.415
    STALE_RECHECK_INTERVAL_MS = 60_000
    MAX_SEARCH_RESULTS       = 10
    MARKER_HIT_RADIUS_PX     = 20
    SEARCH_LOCATE_ZOOM       = 15
    STORAGE_KEY              = "tracker:map:filters"
    LAYER_STORAGE_KEY        = "tracker:map:layers"

    filterGroups       = {}    // group -> visible; groups discovered from device payloads
    persistedFilterGroups = {} // stored states, applied as groups get (re)discovered
    devices            = {}    // id -> Device
    pendingMessages    = {}    // topic -> Array<msg>
    subscriptionStates = {}    // topic -> "connected" | "disconnected" | "rejected"
    seeded             = false
    subscriptions      = []
    markerSource       = new VectorSource()
    staleThresholdSeconds = null
    highlightedDevice  = null
    generation         = 0
    locatedFromParam   = false  // once per instance — survives disconnect() so reconnects don't re-pan

    async connect() {
        const gen = ++this.generation
        this.#restoreFilters()
        this.staleThresholdSeconds = this.#selectedThreshold()
        this.staleInterval = setInterval(() => this.#applyStaleness(), this.STALE_RECHECK_INTERVAL_MS)

        // phones: attribution collapses to an i-button (CC BY "reasonable manner");
        // desktop keeps the always-expanded source line
        const attribution = new Attribution({collapsible: window.matchMedia("(max-width: 575.98px)").matches})
        this.map = new OlMap({
            target: this.element,
            controls: defaultControls({
                attribution: false,
                rotateOptions: {label: this.#compassNeedle(), tipLabel: "Nach Norden ausrichten"}
            }).extend([attribution]),
            view: new View({
                center: fromLonLat(this.MAP_CENTER),
                zoom: this.INITIAL_ZOOM,
                maxZoom: 21
            })
        })

        const layerSwitcher = new LayerSwitcher({
            reverse: false,
            groupSelectStyle: "none",
            activationMode: "click"
        })

        try {
            await this.#setupBaseMaps(gen)
        } catch (e) {
            console.warn("basemap.at unavailable — map renders without base layer", e)
        }
        if (gen !== this.generation) return
        await this.#setupLayers(gen)
        if (gen !== this.generation) return
        this.#setupOverlays()
        this.#restoreLayers()
        this.#watchLayerVisibility()

        this.deckControl = new DeckControl(this.deckTarget, this, this.groupLabelsValue)
        this.map.addControl(this.deckControl)
        this.map.addControl(layerSwitcher)
        this.map.addLayer(new VectorLayer({source: this.markerSource, zIndex: 10}))
        this.markerInfo = new MarkerInfo(this.map, this.element)
        this.locate = new Locate(this.map, this.locateButtonTarget, this.SEARCH_LOCATE_ZOOM)
        this.map.on("pointermove", e => this.#pointerMove(e))
        this.map.on("singleclick", e => this.#mapClick(e))

        // expose for system tests (canvas markers are not DOM-assertable)
        this.element.trackerMap = this

        this.consumer = createConsumer()
        await this.#subscribeAllAndAwaitConnected(gen)
        if (gen !== this.generation) return
        await this.#loadInitial(gen)
        if (gen !== this.generation) return
        this.#locateFromParam()
    }

    disconnect() {
        this.generation++
        clearInterval(this.staleInterval)
        this.subscriptions.forEach(s => s.unsubscribe())
        this.consumer?.disconnect()
        for (const id in this.devices) this.devices[id].dispose()
        this.devices = {}
        this.markerSource.clear()
        this.markerInfo?.dispose()
        this.locate?.dispose()
        if (this.map) {
            this.map.setTarget(null)
            this.map = null
        }
        delete this.element.trackerMap
    }

    addFilterGroup(group) {
        if (this.filterGroups.hasOwnProperty(group)) return
        this.filterGroups[group] = this.persistedFilterGroups[group] !== false
        this.deckControl.addGroup(group)
    }

    updateFilterGroup = (group, visible) => {
        this.filterGroups[group] = visible
        for (const id in this.devices) {
            const device = this.devices[id]
            if (device.filterGroup === group) device.setGroupVisibility(visible)
        }
        this.#persistFilters()
        this.#refreshOpenPopup()
    }

    staleThresholdChanged() {
        this.staleThresholdSeconds = this.#selectedThreshold()
        this.#applyStaleness()
        this.#persistFilters()
    }

    searchInputChanged() {
        this.#clearHighlight()
        this.#renderSearchResults()
    }

    searchEnter(event) {
        event.preventDefault()
        const first = this.#searchMatches()[0]
        if (first) this.#locate(first)
    }

    searchClear() {
        this.searchInputTarget.value = ""
        this.#clearHighlight()
        this.#renderSearchResults()
    }

    #searchMatches() {
        const query = this.searchInputTarget.value.trim().toLowerCase()
        if (query === "") return []
        return Object.values(this.devices).filter(d => d.name?.toLowerCase().includes(query) || d.issi?.includes(query) || d.members.some(m => m.issi?.includes(query))).slice(0, this.MAX_SEARCH_RESULTS)
    }

    #renderSearchResults() {
        const list  = this.searchResultsTarget
        const query = this.searchInputTarget.value.trim()
        list.textContent = ""
        list.classList.toggle("d-none", query === "")
        if (query === "") return

        const matches = this.#searchMatches()
        if (matches.length === 0) {
            const li = document.createElement("li")
            li.classList.add("list-group-item", "text-muted")
            li.textContent = "Kein Treffer — Fahrzeug unbekannt oder keine Position bekannt"
            list.appendChild(li)
            return
        }
        matches.forEach(device => {
            const li = document.createElement("li")
            li.classList.add("list-group-item", "list-group-item-action")
            const img = document.createElement("img")
            img.src = device.markerUrl
            img.classList.add("tracker-search-icon", "me-2")
            const name = document.createElement("span")
            name.textContent = device.name
            const issi = document.createElement("small")
            issi.classList.add("text-muted", "ms-2")
            issi.textContent = device.visible ? device.issi : `${device.issi} · ausgeblendet`
            if (!device.visible) li.classList.add("text-muted")
            li.append(img, name, issi)
            li.addEventListener("click", () => this.#locate(device))
            list.appendChild(li)
        })
    }

    #pointerMove(event) {
        if (event.dragging) { this.element.style.cursor = ""; return }
        this.element.style.cursor = this.#deviceAtPixel(event.pixel) ? "pointer" : ""
    }

    #mapClick(event) {
        const device = this.#deviceAtPixel(event.pixel)
        if (device) this.markerInfo.openFor(device)
        else this.markerInfo.close()
    }

    // geometric hit-test: canvas pixel reads are unreliable with cross-origin icons (tainted
    // hit canvas) and impossible when icon requests fail; closest-feature + pixel distance is immune
    #deviceAtPixel(pixel) {
        const coordinate = this.map.getCoordinateFromPixel(pixel)
        const feature = this.markerSource.getClosestFeatureToCoordinate(coordinate, f => f.get("device").visible)
        if (!feature) return null
        const featurePixel = this.map.getPixelFromCoordinate(feature.getGeometry().getCoordinates())
        // icon is 24x35, anchored bottom-center — hit circle around the icon's visual center
        const dx = pixel[0] - featurePixel[0]
        const dy = pixel[1] - (featurePixel[1] - 17)
        return Math.hypot(dx, dy) <= this.MARKER_HIT_RADIUS_PX ? feature.get("device") : null
    }

    #locate(device) {
        this.#clearHighlight()
        this.highlightedDevice = device
        device.setHighlighted(true)
        const view = this.map.getView()
        view.animate({center: device.feature.getGeometry().getCoordinates(), zoom: Math.max(view.getZoom(), this.SEARCH_LOCATE_ZOOM), duration: 300})
        this.markerInfo.openFor(device)
        this.searchResultsTarget.classList.add("d-none")
    }

    #locateFromParam() {
        if (this.locatedFromParam) return
        this.locatedFromParam = true
        const issi = this.locateIssiValue
        if (!issi) return
        // member radios (e.g. crew handhelds) render only via their resource's main-terminal marker
        const device = this.devices[issi] || Object.values(this.devices).find(d => d.members.some(m => m.issi === issi))
        if (device) this.#locate(device)
    }

    #refreshOpenPopup() {
        if (!this.markerInfo) return // stale interval can fire before connect() finishes awaiting layers
        const device = this.devices[this.markerInfo.deviceId]
        if (device) this.markerInfo.refresh(device)
    }

    #clearHighlight() {
        if (!this.highlightedDevice) return
        this.highlightedDevice.setHighlighted(false)
        this.highlightedDevice = null
        this.#refreshOpenPopup() // after setHighlighted(false): visible must reflect the un-highlighted state
    }

    #selectedThreshold() {
        const value = this.staleThresholdTarget.value
        return value === "" ? null : Number(value)
    }

    #persistFilters() {
        // merge: groups not discovered this session keep their stored state
        const groups = {...this.persistedFilterGroups, ...this.filterGroups}
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify({filterGroups: groups, staleThreshold: this.staleThresholdSeconds}))
        } catch { /* storage unavailable — filters just won't persist */ }
    }

    #restoreFilters() {
        let data
        try { data = JSON.parse(localStorage.getItem(this.STORAGE_KEY)) } catch { return }
        if (!data) return

        this.persistedFilterGroups = data.filterGroups || {}

        if (data.staleThreshold !== undefined) {
            const value = data.staleThreshold === null ? "" : String(data.staleThreshold)
            if (Array.from(this.staleThresholdTarget.options).some(o => o.value === value)) this.staleThresholdTarget.value = value
            // not in the rendered list (gated/changed) — select stays at the default first option
        }
    }

    #baseLayers() { return this.baseGroup?.getLayers().getArray() ?? [] }
    // #overlayEntries() { return [this.novarockGroup, this.geofenceLayer] } // novarock overlay disabled until next event
    #overlayEntries() { return [this.geofenceLayer] }

    #watchLayerVisibility() {
        [...this.#baseLayers(), ...this.#overlayEntries()].forEach(layer => layer.on("change:visible", () => this.#persistLayers()))
    }

    #persistLayers() {
        const base = this.#baseLayers().find(l => l.getVisible())?.get("title")
        const overlays = {}
        this.#overlayEntries().forEach(l => overlays[l.get("title")] = l.getVisible())
        try { localStorage.setItem(this.LAYER_STORAGE_KEY, JSON.stringify({base: base, overlays: overlays})) } catch { /* storage unavailable */ }
    }

    #restoreLayers() {
        let data
        try { data = JSON.parse(localStorage.getItem(this.LAYER_STORAGE_KEY)) } catch { return }
        if (!data) return

        const target = this.#baseLayers().find(l => l.get("title") === data.base)
        if (target) this.#baseLayers().forEach(l => l.setVisible(l === target))

        for (const title in data.overlays || {}) {
            const layer = this.#overlayEntries().find(l => l.get("title") === title)
            layer?.setVisible(data.overlays[title]) // unknown titles dropped
        }
    }

    #isStale(device) {
        if (this.staleThresholdSeconds === null) return false
        return device.positionAt < new Date(Date.now() - this.staleThresholdSeconds * 1000)
    }

    #applyStaleness() {
        for (const id in this.devices) {
            const device = this.devices[id]
            device.setStale(this.#isStale(device))
        }
        this.#refreshOpenPopup()
    }

    #subscribeAllAndAwaitConnected(gen) {
        return Promise.all(
            this.topicsValue.map(topic => new Promise(resolve => {
                this.pendingMessages[topic] = []
                let firstConnect = true
                const sub = this.consumer.subscriptions.create(
                    {channel: "Tracker::PositionsChannel", topic: topic},
                    {
                        connected: () => {
                            this.#setSubscriptionState(topic, "connected")
                            if (firstConnect) {
                                firstConnect = false
                                resolve()
                                return
                            }
                            // Reconnect after a drop — re-snapshot to catch up.
                            if (gen !== this.generation) return
                            this.#resnapshot(gen)
                        },
                        disconnected: () => {
                            this.#setSubscriptionState(topic, "disconnected")
                            this.seeded = false
                            this.pendingMessages[topic].length = 0
                        },
                        rejected: () => {
                            this.#setSubscriptionState(topic, "rejected")
                            // Server denied subscription. Snapshot is authoritative.
                            if (firstConnect) { firstConnect = false; resolve(); return }
                            if (gen !== this.generation) return
                            this.#resnapshot(gen)
                        },
                        received: (msg) => {
                            if (!this.seeded) {
                                this.pendingMessages[topic].push(msg)
                                return
                            }
                            this.#handleMessage(msg, topic)
                        }
                    }
                )
                this.subscriptions.push(sub)
            }))
        )
    }

    async #loadInitial(gen) {
        const res = await fetch(this.devicesUrlValue, {headers: {Accept: "application/json"}})
        if (gen !== this.generation) return
        if (!res.ok) {
            this.#clearAllDevices()
            this.seeded = true
            return
        }
        const payload = await res.json()
        if (gen !== this.generation) return
        this.#reconcileDevices(payload)
        this.seeded = true
        this.#drainBuffers()
    }

    async #resnapshot(gen) {
        this.seeded = false
        await this.#loadInitial(gen)
    }

    #reconcileDevices(payload) {
        const incomingIds = new Set(payload.map(d => d.id))
        for (const id in this.devices) {
            if (!incomingIds.has(id)) this.#removeDevice(id)
        }
        payload.forEach(d => this.#upsertDevice(d))
    }

    #clearAllDevices() {
        for (const id in this.devices) this.#removeDevice(id)
    }

    #drainBuffers() {
        for (const topic in this.pendingMessages) {
            const msgs = this.pendingMessages[topic]
            msgs.forEach(m => this.#handleMessage(m, topic))
            msgs.length = 0
        }
    }

    #handleMessage(msg, topic) {
        switch (msg.type) {
            case "upsert":      return this.#upsertDevice(msg.device, topic)
            case "disappeared": return this.#withdrawTopic(msg.device_id, topic)
        }
    }

    // topic: justifying subscription for stream messages; null for snapshot rows
    // (snapshot rows carry payload.topics instead).
    #upsertDevice(payload, topic = null) {
        if (!payload) return
        const existing = this.devices[payload.id]
        if (existing && existing.positionAt && new Date(payload.position_at) < existing.positionAt) {
            if (payload.topics) existing.setTopics(payload.topics)  // snapshot stays authoritative for justification
            existing.addTopic(topic)  // stale payload, but the visibility claim holds
            return
        }
        let device = existing
        if (!device) {
            device = new Device(payload)
            this.devices[payload.id] = device
            this.markerSource.addFeature(device.feature)
            this.addFilterGroup(device.filterGroup)
        } else {
            device.applyFull(payload)
        }
        if (payload.topics) device.setTopics(payload.topics)
        device.addTopic(topic)
        device.setGroupVisibility(this.filterGroups[device.filterGroup] !== false)
        device.setStale(this.#isStale(device))
        this.markerInfo.refresh(device)
        // resource payloads carry their member ISSIs — drop ghost individual markers
        // (a terminal newly claimed by the resource, or a re-keyed old main)
        device.members.forEach(m => { if (m.issi !== device.id) this.#removeDevice(m.issi) })
    }

    #withdrawTopic(id, topic) {
        const device = this.devices[id]
        if (!device) return
        if (device.removeTopic(topic)) this.#removeDevice(id)
    }

    #removeDevice(id) {
        const device = this.devices[id]
        if (!device) return
        device.dispose()
        this.markerSource.removeFeature(device.feature)
        delete this.devices[id]
        this.markerInfo.closeIfDevice(id)
        if (this.highlightedDevice?.id === id) this.highlightedDevice = null
    }

    #setSubscriptionState(topic, state) {
        this.subscriptionStates[topic] = state
        this.#renderConnectionStatus()
    }

    #renderConnectionStatus() {
        if (!this.hasConnectionStatusTarget) return
        const state = this.#computeConnectionState()
        const label = TrackerMapController.CONNECTION_STATUS_LABELS[state]
        const target = this.connectionStatusTarget
        target.dataset.state = state
        target.title = label
        const labelEl = target.querySelector(".connection-status-label")
        if (labelEl) labelEl.textContent = label
    }

    #computeConnectionState() {
        const expected = this.topicsValue.length
        const states   = Object.values(this.subscriptionStates)
        if (states.includes("rejected"))     return "denied"
        if (states.includes("disconnected")) return "disconnected"
        if (states.length < expected)        return "connecting"
        return "connected"
    }

    async #getWMTSOptions(layer, tilePixelRatio) {
        if (!this.wmtsCapabilities) {
            const response       = await fetch(this.CAPABILITIES_URL)
            const text           = await response.text()
            this.wmtsCapabilities = new WMTSCapabilities().read(text)
        }
        const options              = optionsFromCapabilities(this.wmtsCapabilities, {layer: layer, matrixSet: "google3857", style: "normal"})
        if (tilePixelRatio) options.tilePixelRatio = tilePixelRatio
        options.attributions = "Datenquelle: <a target='_blank' href='https://basemap.at/'>basemap.at</a>"
        return options
    }

    async #setupBaseMaps(gen) {
        const hiDPI          = DEVICE_PIXEL_RATIO > 1
        const layer          = hiDPI ? "bmaphidpi" : "geolandbasemap"
        const tilePixelRatio = hiDPI ? 2 : 1

        this.orthoLayer = new TileLayer({
            title: "Basemap.at Orthofoto",
            type: "base",
            source: new WMTS(await this.#getWMTSOptions("bmaporthofoto30cm")),
            zIndex: 0,
            visible: false
        })
        this.baseGroup = new Group({
            title: "Karte",
            layers: [
                new TileLayer({
                    title: "Basemap.at",
                    type: "base",
                    source: new WMTS(await this.#getWMTSOptions(layer, tilePixelRatio)),
                    zIndex: 0,
                    visible: true
                }),
                this.orthoLayer
            ]
        })
        if (gen !== this.generation) return
        this.map.addLayer(this.baseGroup)
    }

    async #setupLayers(gen) {
        const [districtData, cadastralData] = await Promise.all([
            fetch(this.DISTRICT_BOUNDARIES_URL).then(r => r.json()),
            fetch(this.CADASTRAL_BOUNDARIES_URL).then(r => r.json())
        ])
        if (gen !== this.generation) return
        const districtSource  = new VectorSource({features: new GeoJSON().readFeatures(districtData,  {featureProjection: 'EPSG:3857'})})
        const cadastralSource = new VectorSource({features: new GeoJSON().readFeatures(cadastralData, {featureProjection: 'EPSG:3857'})})

        const districtLayer = new VectorLayer({
            source: districtSource,
            zIndex: 1,
            maxZoom: this.ZOOM_LEVEL_FOR_CADASTRAL,
            style: new Style({stroke: new Stroke()})
        })

        const cadastralLayer = new VectorLayer({
            source: cadastralSource,
            zIndex: 1,
            minZoom: this.ZOOM_LEVEL_FOR_CADASTRAL,
            style: new Style({stroke: new Stroke()})
        })

        this.map.addLayer(new Group({
            layers: [districtLayer, cadastralLayer]
        }))
    }

    #setupOverlays() {
        this.geofenceSource = new VectorSource({features: this.geofencesValue.map(f => this.#fenceFeature(f))})
        this.geofenceLayer = new VectorLayer({title: "Geofences", zIndex: 3, visible: true, source: this.geofenceSource})
        // this.map.addLayer(new Group({title: "Overlays", layers: [this.#buildNovarockGroup(), this.geofenceLayer]})) // novarock overlay disabled until next event
        this.map.addLayer(new Group({title: "Overlays", layers: [this.geofenceLayer]}))
    }

    // novarock overlay disabled until next event — restore this block and its two constants, #overlayEntries and #setupOverlays wiring
    // #buildNovarockGroup() {
    //     const extent = transformExtent(this.NOVAROCK_EXTENT_LONLAT, "EPSG:4326", "EPSG:3857")
    //     this.novarockUavLayer  = new TileLayer({source: new XYZ({url: `${this.NOVAROCK_TILE_BASE}/uav/{z}/{x}/{y}.webp`}), extent: extent, zIndex: 2, visible: false})
    //     this.novarockPlanLayer = new TileLayer({source: new XYZ({url: `${this.NOVAROCK_TILE_BASE}/planpdf/{z}/{x}/{y}.webp`}), extent: extent, zIndex: 2})
    //     this.novarockGroup = new Group({title: "Novarock", combine: true, visible: false, layers: [this.novarockUavLayer, this.novarockPlanLayer]})
    //     this.#bindUavToOrtho()
    //     return this.novarockGroup
    // }

    // #bindUavToOrtho() {
    //     if (!this.orthoLayer) return // basemap setup failed (offline / tests) — UAV stays hidden
    //     this.novarockUavLayer.setVisible(this.orthoLayer.getVisible())
    //     this.orthoLayer.on("change:visible", () => this.novarockUavLayer.setVisible(this.orthoLayer.getVisible()))
    // }

    #fenceFeature({name, polygon}) {
        const ring = polygon.map(c => fromLonLat(c))
        if (ring.length && (ring[0][0] !== ring.at(-1)[0] || ring[0][1] !== ring.at(-1)[1])) ring.push(ring[0])
        const feature = new Feature({geometry: new Polygon([ring])})
        feature.setStyle(new Style({
            stroke: new Stroke({color: "#0d6efd", width: 2, lineDash: [10, 6]}),
            text: new Text({text: name, font: "bold 12px sans-serif", fill: new Fill({color: "#0d6efd"}), stroke: new Stroke({color: "#ffffff", width: 3})})
        }))
        return feature
    }

    // Rotate-control label: span wrapper (HTMLElement per the OL label contract) around a
    // needle SVG — OL adds .ol-compass and rotates it with the view. fill via style attr:
    // var() is invalid in SVG presentation attributes.
    #compassNeedle() {
        const span = document.createElement("span")
        span.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">'
            + '<polygon points="12,2 16,12 8,12" style="fill: var(--cui-danger, #dc3545)"/>'
            + '<polygon points="8,12 16,12 12,22" style="fill: var(--cui-secondary-color, #6c757d)"/>'
            + '</svg>'
        return span
    }
}
